/**
 * DataImportView -- non-SoR CSV upload + dry-run review + commit (P117.3).
 *
 * Route: /admin/data-import
 *
 * Answers "what happens when we only have a CSV": pick an entity, upload
 * a CSV, see a per-row dry-run report (verdict + skip reasons, insert vs
 * update), then commit the OK rows. Nothing touches live tables until
 * Commit. A SAMA-completeness scorecard at the top shows how much of the
 * estate already has the SAMA-mandated fields populated.
 *
 * Admin-only; hardcoded English to match the sibling shared/admin views.
 */
import { entityKindLabel } from "shared/vocab/entityKind";
import type { TFunction } from "i18next";
import { said } from "shared/i18n/said";
import { listOf } from "shared/i18n/listOf";
import { useTranslation } from "react-i18next";
import { groupedCount, i18n } from "shared/i18n";
import { formatDateTime } from "shared/time/formatDate";
import { useEffect, useState } from "react";
import { refusalMessage } from "shared/api/refusalMessage";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";
import { useQueryClient } from "@tanstack/react-query";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { EngineBusy } from "shared/components/EngineBusy";
import { useSearchParams } from "react-router-dom";
import { useMe } from "shared/api/useMe";
import {
  downloadBatchOutcome,
  ImportError,
  importKeys,
  useCommitBatch,
  useDiscardImportBatch,
  useImportBatch,
  useImportBatches,
  useImportCompleteness,
  useImportDictionary,
  ImportUndoUnsure,
  useUndoImportBatch,
  useUploadCsv,
} from "shared/api/hooks/useImportDictionary";
import type {
  ImportBatch,
  ImportBatchSummary,
  ImportEntityCompleteness,
} from "shared/api/types";
import { pathTrailText } from "shared/components/PathTrail";
import { useToast } from "shared/toast/ToastProvider";

import { Arrow } from "shared/components/Arrow";
import { Link } from "react-router-dom";

/** ROUND 15 E9 -- a count in running text, grouped for the reader's locale. */
function grouped(n: number): string {
  return String(groupedCount(n, i18n.language));
}
function pctTone(pct: number): string {
  if (pct >= 80) return "text-emerald-700";
  if (pct >= 40) return "text-amber-700";
  return "text-rose-700";
}

function CompletenessPanel() {
  const { t } = useTranslation();
  const data = useImportCompleteness();
  if (!data.data || data.data.entities.length === 0) return null;
  return (
    <section
      data-testid="import-completeness"
      className="mb-6 rounded-lg border border-slate-200 bg-white p-4"
    >
      <h2 className="mb-1 text-base font-semibold text-slate-900">
        {t("screens.dataImportView.samaMandatedFieldCompleteness")}
      </h2>
      <p className="mb-3 text-xs text-slate-500">
        {t("screens.dataImportView.howMuchOfTheLiveEstateHas")}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {data.data.entities.map((e: ImportEntityCompleteness) => (
          <div
            key={e.kind}
            data-testid={`completeness-${e.kind}`}
            className="rounded border border-slate-100 p-3"
          >
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-slate-800">
                {said(t, `screens.dataImportView.entityLabel.${e.kind}`, e.label)}
              </span>
              {/* Round 18 F10 -- no percentage over nothing: "0/0 ·
                  0%" stood for every mandated field on an empty estate. */}
              {e.total_rows === 0 ? (
                <span
                  data-testid={`completeness-empty-${e.kind}`}
                  className="text-xs text-slate-500"
                >
                  {t("screens.dataImportView.nothingLoaded")}
                </span>
              ) : (
                <span
                  className={`text-sm font-semibold tabular-nums ${pctTone(e.overall_pct)}`}
                >
                  {e.overall_pct}%
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[11px] text-slate-400">
              {e.total_rows} {t("screens.dataImportView.rows")}
            </p>
            <ul className="mt-2 space-y-1">
              {(e.total_rows === 0 ? [] : e.fields).map((f) => (
                <li
                  key={f.name}
                  className="flex items-baseline justify-between text-[11px]"
                >
                  <span className="font-mono text-slate-600">{f.name}</span>
                  <span className={`tabular-nums ${pctTone(f.pct)}`}>
                    {f.populated}/{f.total} · {f.pct}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

//: D10 -- where each imported kind can be read back.
//:
//: Deliberately a map rather than a pattern: the register routes are
//: not uniform, and a guessed URL that 404s is a worse ending than the
//: dead end it replaces. Kinds absent here (the graph-edge links) have
//: no register to land on and get no link.
/* ROUND 13 D3 -- the limits the server enforces, said where a file is
   chosen. They mirror `_MAX_UPLOAD_BYTES` and `MAX_IMPORT_ROWS` in
   `backend/foundation/ingest`; the server stays the thing that decides,
   and this is what a person is told before they find out. */
const MAX_UPLOAD_MB = 32;
const MAX_IMPORT_ROWS = 50000;

const ADMIN_REGISTER_PATH: Record<string, string> = {
  service: "/foundation/admin/services",
  ibs_service: "/foundation/admin/ibses",
  capability: "/foundation/admin/capabilities",
  vendor: "/foundation/admin/vendors",
  product: "/foundation/admin/products",
  configuration_item: "/foundation/admin/configuration-items",
  channel: "/foundation/admin/channels",
  outsourcing_contract: "/foundation/admin/outsourcing-contracts",
  // A6 -- both have a register and neither was listed, so a data-asset
  // or journey import ended where the edges did.
  data_asset: "/foundation/admin/data-assets",
  funding_resource: "/foundation/admin/funding-resources",
  ibs_group: "/foundation/admin/ibs-groups",
  regulatory_commitment: "/foundation/admin/regulatory-commitments",
  journey: "/foundation/admin/journeys",
  // ROUND 13 D9 -- the last two importable entities with nowhere to
  // land. Both were modelled by P371 and neither had a register, so a
  // CSV import of either ended at "Committed" and stopped.
  recovery_team: "/foundation/admin/recovery-teams",
  facility: "/foundation/admin/facilities",
};

function EngineMappingPanel({ batch }: { batch: ImportBatch }) {
  const { t } = useTranslation();
  const m = batch.engine_mapping;
  if (!m) return null;
  return (
    <section
      data-testid="import-engine-mapping"
      className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/40 p-4"
    >
      <h2 className="mb-1 text-sm font-semibold text-slate-900">
        {t("screens.dataImportView.engineColumnMapping")}
      </h2>
      <p className="mb-3 text-[11px] text-slate-500">
        {t("screens.dataImportView.howTheMarsadEngine")}
        {m.model ? ` (${m.model})` : ""}{" "}
        {t("screens.dataImportView.mappedYourSourceColumnsOntoTheField")}
      </p>
      <ul className="space-y-1 text-xs">
        {m.mappings.map((x) => (
          <li
            key={x.target_field}
            data-testid={`mapping-${x.target_field}`}
            className="flex items-baseline gap-2"
          >
            <span className="font-mono text-slate-600">{x.source_column}</span>
            <span className="text-slate-400">
              <Arrow />
            </span>
            <span className="font-mono font-medium text-slate-800">
              {x.target_field}
            </span>
            <span className="text-[10px] tabular-nums text-indigo-700">
              {Math.round(x.confidence * 100)}%
            </span>
          </li>
        ))}
      </ul>
      {m.unmapped_source_columns.length > 0 && (
        <p className="mt-2 text-[11px] text-slate-500">
          {t("screens.dataImportView.ignoredSourceColumns")}{" "}
          {m.unmapped_source_columns.join(", ")}
        </p>
      )}
      {m.enum_overrides && Object.keys(m.enum_overrides).length > 0 && (
        <div
          data-testid="import-enum-overrides"
          className="mt-2 text-[11px] text-slate-600"
        >
          <span className="font-medium text-slate-700">
            {t("screens.dataImportView.resolvedEnumSynonyms")}
          </span>
          <ul className="mt-0.5 space-y-0.5">
            {Object.entries(m.enum_overrides).flatMap(([field, pairs]) =>
              Object.entries(pairs).map(([messy, canonical]) => (
                <li key={`${field}:${messy}`} className="font-mono">
                  {field}
                  {t("screens.dataImportView.quot")}
                  {messy}
                  {t("screens.dataImportView.quot2")}{" "}
                  <span className="text-slate-400">
                    <Arrow />
                  </span>{" "}
                  {canonical}
                </li>
              )),
            )}
          </ul>
        </div>
      )}
    </section>
  );
}

function BatchReview({
  batch,
  onCommit,
  committing,
}: {
  batch: ImportBatch;
  onCommit: () => void;
  committing: boolean;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const committed = batch.status === "committed";
  // Where the rows he just created live. Only for the kinds that have a
  // register of their own; a graph edge has none, and offering a link to
  // an approximate place is worse than offering none.
  const register = ADMIN_REGISTER_PATH[batch.entity_kind] ?? null;
  const ignored = batch.ignored_columns ?? [];
  const selfRefs = batch.self_referencing_rows ?? 0;
  const notListed = batch.rows_not_listed ?? 0;
  const seenBefore = batch.previously_committed_at ?? null;
  const duplicateNames = batch.duplicate_name_rows ?? 0;
  /* Round 11 F4, eighth round -- "See the row you imported" landed on
     the REGISTER, which on this estate is a list of dozens and leaves
     the operator doing the finding the sentence promised to do.
     The row already knows where it went: `committed_target_id` has
     been on the wire since the commit path started writing it, and
     the register opens a row on `?edit=<id>`.

     One row opens; several land on the register, because there is no
     honest way to open five at once and the plural sentence has never
     claimed to. */
  const landed = (batch.rows ?? [])
    .map((r) => r.committed_target_id)
    .filter((id): id is string => Boolean(id));
  /* Round 12 A6 -- the comment above said a graph edge has no register
     and that linking to an approximate place is worse than none. Both
     true, and it left four of the seventeen importable kinds ending at
     a past participle. An edge DOES have a place: the drawer of the
     entity it hangs off, where the link section lists it. The server
     names that anchor per row, so this is the edge's own destination
     rather than an approximation of one. */
  const parents = (batch.rows ?? [])
    .filter((r) => r.committed_target_id && r.committed_parent_id)
    .map((r) => ({ kind: r.committed_parent_kind ?? "", id: r.committed_parent_id! }));
  const firstParent = parents[0];
  const parentRegister = firstParent
    ? (ADMIN_REGISTER_PATH[firstParent.kind] ?? null)
    : null;
  // Every row hanging off ONE anchor opens that anchor; several anchors
  // land on its register, because there is no honest way to open two
  // drawers at once.
  const oneParent =
    parents.length > 0 && parents.every((p) => p.id === firstParent!.id);
  const onwardTo =
    register && committed && landed.length === 1
      ? `${register}?edit=${encodeURIComponent(landed[0]!)}`
      : register
        ? register
        : committed && parentRegister
          ? oneParent
            ? `${parentRegister}?edit=${encodeURIComponent(firstParent!.id)}`
            : parentRegister
          : null;
  return (
    <section
      data-testid="import-batch-review"
      className="rounded-lg border border-slate-200 bg-white p-4"
    >
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {/* ROUND 15 E9 -- grouped, as every count now is. */}
        <div className="text-sm text-slate-700" data-testid="import-batch-counts">
          <span className="font-semibold">{grouped(batch.row_count)}</span>{" "}
          {/* Round 16 F8 -- "1 rows". */}
          {t("screens.dataImportView.rowsWord", { count: batch.row_count })}{" "}
          <span className="text-emerald-700">
            {grouped(batch.ok_count)} {t("screens.dataImportView.ok")}
          </span>{" "}
          ·{" "}
          <span className="text-rose-700">
            {grouped(batch.error_count)}{" "}
            {t("screens.dataImportView.errorsWord", { count: batch.error_count })}
          </span>{" "}
          · {grouped(batch.insert_count)} {t("screens.dataImportView.insert")}{" "}
          {grouped(batch.update_count)} {t("screens.dataImportView.update")}
        </div>
        {/* Round 20 W8 -- every row's outcome, as a file the bank can
            reconcile its own against: the list below stops at two
            hundred clean rows. */}
        <button
          type="button"
          data-testid="import-download-outcome"
          onClick={() => {
            downloadBatchOutcome(batch.id).catch((err: unknown) =>
              toast.push({
                tone: "error",
                message: t("screens.dataImportView.outcomeDownloadFailed"),
                detail: err instanceof Error ? err.message : String(err),
              }),
            );
          }}
          className="ms-auto rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm text-brand-brassDark hover:bg-brand-brass/10"
        >
          {t("screens.dataImportView.downloadOutcome", { count: batch.row_count })}
        </button>
        <button
          type="button"
          data-testid="import-commit"
          onClick={onCommit}
          disabled={committed || committing || batch.ok_count === 0}
          className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {committed ? (
            t("screens.dataImportView.committed")
          ) : committing ? (
            /* ROUND 13 D9 -- the screen said nothing between the press
               and the answer, and the answer is 75 seconds away on a
               15,000-row commit. A static label for over a minute is
               how an operator decides the product has hung and presses
               again. */
            <EngineBusy
              label={t("screens.dataImportView.committing")}
              testid="import-commit-busy"
            />
          ) : (
            t("screens.dataImportView.commitRows", { count: batch.ok_count })
          )}
        </button>
      </header>
      {/* ROUND 13 D8 -- the same file committed twice.
          It was reported as "0 insert - 1 update" with no mention of
          the first commit, while `import_batches.source_sha256` held
          the matching hash and nothing read it. "1 update" on a second
          upload is indistinguishable from "somebody edited a row",
          which is the question the operator is actually asking.

          FIRST in the panel, above the ignored-columns notice, because
          it is the one that may mean "do not press Commit at all". */}
      {seenBefore && (
        <p
          data-testid="import-previously-committed"
          className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          {t("screens.dataImportView.previouslyCommitted", {
            when: formatDateTime(seenBefore, i18n.language),
          })}
        </p>
      )}
      {/* ROUND 13 D8 -- two things under one name.
          They committed with a clean verdict and no mention of either,
          and field-exam readiness then printed two identical lines,
          because it renders by name. Amber rather than rose: a bank may
          legitimately run two services called "Settlement", so this
          says what it found and leaves the call to the operator. */}
      {duplicateNames > 0 && (
        <p
          data-testid="import-duplicate-names"
          className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          {t("screens.dataImportView.duplicateNames", { count: duplicateNames })}
        </p>
      )}
      {/* ROUND 13 D1 -- the columns nobody read.

          A file whose `service_owner` header had been renamed to
          `Service Owner` reported "1 rows - 1 ok - 0 errors - 1 insert"
          and put a service in the register with no owner. The count of
          what was NOT read has to sit beside the count of what was, or
          a clean verdict is a receipt for a silent loss.

          Above the commit table rather than below it: a person who has
          already pressed Commit has been told too late. */}
      {ignored.length > 0 && (
        <p
          data-testid="import-ignored-columns"
          className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          {t("screens.dataImportView.ignoredColumnsWarning", {
            count: ignored.length,
          })}{" "}
          {ignored.map((c, i) => (
            <span key={c.column}>
              {i > 0 ? ", " : ""}
              {/* Round 18 F10 -- a column name is the file's, in its own
                  direction: isolated so an RTL line does not reorder it. */}
              <bdi className="font-mono">{c.column}</bdi>
              {c.retired_for ? (
                <span className="text-amber-700">
                  {" "}
                  {t("screens.dataImportView.retiredColumn", {
                    field: c.retired_for,
                  })}
                </span>
              ) : c.suggestion ? (
                <span className="text-amber-700">
                  {" "}
                  {t("screens.dataImportView.didYouMean", {
                    field: c.suggestion,
                  })}
                </span>
              ) : null}
            </span>
          ))}
        </p>
      )}
      {/* ROUND 13 D2 -- said out loud, because the operator's previous
          experience of this file was a refusal. A 1,750-row hierarchy
          took five uploads, with nothing saying a second pass was
          needed or when it was done; silence now would be an
          improvement nobody could see. */}
      {selfRefs > 0 && (
        <p
          data-testid="import-self-referencing"
          className="mb-3 text-xs text-slate-600"
        >
          {t("screens.dataImportView.selfReferencingRows", { count: selfRefs })}
        </p>
      )}
      {/* D10 -- what you just imported, six rounds. The panel ended at
          "Committed" and left the operator on a staging screen with no
          route to the rows he had just created, so the last thing the
          product said about his work was a past participle. */}
      {committed && onwardTo && (
        <p
          data-testid="import-committed-onward"
          className="border-t border-paper-2 px-3 py-2 text-xs text-slate-600"
        >
          <Link to={onwardTo} className="text-brand-brassDark hover:underline">
            {t(
              register
                ? "screens.dataImportView.seeWhatLanded"
                : "screens.dataImportView.seeWhereEdgesLanded",
              { count: batch.ok_count },
            )}
          </Link>
        </p>
      )}
      {/* ROUND 13 D9 -- the listing is capped at two hundred clean rows,
          and a table that quietly showed a fifth of a file would be the
          same defect D1 is about. Every ERROR row is always listed. */}
      {notListed > 0 && (
        <p
          data-testid="import-rows-not-listed"
          className="mb-2 text-[11px] text-slate-500"
        >
          {t("screens.dataImportView.rowsNotListed", { count: notListed })}
        </p>
      )}
      <div className="max-h-[28rem] overflow-y-auto">
        <table className="w-full text-start text-xs">
          <thead className="sticky top-0 bg-white">
            <tr className="text-[11px] uppercase tracking-wide text-slate-400">
              <th className="pb-1 pe-2 font-medium">#</th>
              <th className="pb-1 pe-2 font-medium">
                {t("screens.dataImportView.verdict")}
              </th>
              <th className="pb-1 pe-2 font-medium">
                {t("screens.dataImportView.action")}
              </th>
              <th className="pb-1 pe-2 font-medium">
                {t("screens.dataImportView.key")}
              </th>
              <th className="pb-1 font-medium">{t("screens.dataImportView.detail")}</th>
            </tr>
          </thead>
          <tbody>
            {batch.rows.map((r) => (
              <tr
                key={r.row_number}
                data-testid={`import-row-${r.row_number}`}
                className="border-t border-slate-100 align-top"
              >
                <td className="py-1 pe-2 tabular-nums text-slate-500">
                  {r.row_number}
                </td>
                <td className="py-1 pe-2">
                  <span
                    data-verdict={r.verdict}
                    className={
                      "rounded px-1.5 py-0.5 text-[11px] font-medium " +
                      (r.verdict === "ok"
                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                        : "bg-rose-50 text-rose-700 ring-1 ring-rose-200")
                    }
                  >
                    {r.verdict}
                  </span>
                </td>
                <td className="py-1 pe-2 text-slate-600">{r.action ?? "—"}</td>
                <td className="py-1 pe-2 font-mono text-slate-700">
                  {r.target_code ?? "—"}
                </td>
                <td className="py-1 text-slate-600">
                  {r.errors.length > 0 ? (
                    <ul className="text-rose-700">
                      {/* ROUND 13 I2 -- the reason a row was refused,
                          in the reader's language. These were composed
                          on the server as formatted strings with a
                          Python literal inside them and reachable by
                          no catalogue at all, on the screen where a
                          person is most likely to need words they
                          understand: whoever prepared the spreadsheet
                          reads this, and it is the only thing here
                          that tells them what to change.

                          The server's English is the fallback, so a
                          code the catalogue has not learned yet still
                          reads. */}
                      {r.errors.map((e, i) => (
                        <li key={i} data-code={e.code}>
                          {e.field ? (
                            <>
                              <span className="font-mono">{e.field}</span>:{" "}
                            </>
                          ) : null}
                          {e.code
                            ? t(e.code, {
                                ...(e.params ?? {}),
                                ...rowErrorWords(t, e.params ?? {}),
                                defaultValue: e.message,
                              })
                            : e.message}
                        </li>
                      ))}
                    </ul>
                  ) : (r.warnings ?? []).length > 0 ? (
                    /* ROUND 13 D8 -- amber, not rose, and the row still
                       commits. Two services under one name may be real;
                       what was wrong was committing them silently. */
                    <ul
                      className="text-amber-800"
                      data-testid={`import-warn-${r.row_number}`}
                    >
                      {(r.warnings ?? []).map((w, i) => (
                        <li key={i}>
                          <span className="font-mono">{w.field}</span>: {w.message}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** ROUND 13 D8 -- the importer's history, and the two verbs it lacked.
 *
 * Fifteen staged batches sat on one estate invisible to every screen,
 * `?batch=<id>` was ignored, and there was no discard and no undo -- so
 * a mis-typed upload stayed forever and the only remedy for committing
 * the wrong file was to find every row it touched by hand.
 */
function BatchHistory({
  kind,
  onOpen,
  onDiscarded,
}: {
  kind: string;
  onOpen: (id: string) => void;
  /** Round 20 W9 -- so the page stops offering a batch that is gone. */
  onDiscarded?: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const history = useImportBatches(kind);
  const discard = useDiscardImportBatch();
  const undo = useUndoImportBatch();
  // Round 14 F1 -- undo removes records and puts others back, and it
  // acted on one press. It asks first now.
  const [confirmUndo, setConfirmUndo] = useState<string | null>(null);
  const rows = history.data ?? [];

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: importKeys.batches(kind) });
    void qc.invalidateQueries({ queryKey: importKeys.completeness });
  };

  if (rows.length === 0) return null;
  return (
    <section
      data-testid="import-history"
      className="mb-6 rounded-lg border border-slate-200 bg-white p-4"
    >
      <h2 className="mb-2 text-sm font-semibold text-slate-800">
        {t("screens.dataImportView.historyHeading")}
      </h2>
      <table className="w-full text-start text-xs">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-slate-400">
            <th className="pb-1 pe-2 font-medium">
              {t("screens.dataImportView.historyFile")}
            </th>
            <th className="pb-1 pe-2 font-medium">
              {t("screens.dataImportView.historyWhen")}
            </th>
            <th className="pb-1 pe-2 font-medium">
              {t("screens.dataImportView.historyOutcome")}
            </th>
            <th className="pb-1 font-medium" />
          </tr>
        </thead>
        <tbody>
          {rows.map((b: ImportBatchSummary) => (
            <tr
              key={b.id}
              data-testid={`import-history-row-${b.id}`}
              className="border-t border-slate-100 align-top"
            >
              <td className="py-1 pe-2 text-slate-700">
                {b.filename ?? t("screens.dataImportView.historyNoFilename")}
                {/* Round 20 W13 -- a staged batch of another kind is listed
                    too, so it says what it is. */}
                {b.entity_kind !== kind && (
                  <span
                    data-testid={`import-history-kind-${b.id}`}
                    className="ms-1 rounded bg-slate-100 px-1 text-[10px] text-slate-500"
                  >
                    {said(
                      t,
                      `screens.dataImportView.entityLabel.${b.entity_kind}`,
                      b.entity_kind,
                    )}
                  </span>
                )}
              </td>
              <td className="py-1 pe-2 text-slate-500">
                {formatDateTime(b.created_at, i18n.language)}
              </td>
              <td className="py-1 pe-2 text-slate-600">
                {t(`screens.dataImportView.historyStatus_${b.status}`, {
                  defaultValue: b.status,
                })}{" "}
                ·{" "}
                {t("screens.dataImportView.historyCounts", {
                  ok: b.ok_count,
                  errors: b.error_count,
                  rows: b.row_count,
                })}
              </td>
              <td className="py-1 text-end">
                <button
                  type="button"
                  data-testid={`import-history-open-${b.id}`}
                  onClick={() => onOpen(b.id)}
                  className="text-brand-brassDark hover:underline"
                >
                  {t("screens.dataImportView.historyOpen")}
                </button>
                {b.status === "staged" && (
                  <button
                    type="button"
                    data-testid={`import-history-discard-${b.id}`}
                    disabled={discard.isPending}
                    onClick={() =>
                      discard.mutate(b.id, {
                        onSuccess: () => {
                          refresh();
                          onDiscarded?.(b.id);
                          toast.push({
                            tone: "success",
                            message: t("screens.dataImportView.historyDiscarded"),
                          });
                        },
                        onError: (err) =>
                          toast.push({
                            tone: "error",
                            message: t("screens.dataImportView.historyDiscardFailed"),
                            detail: refusalMessage(t, err),
                          }),
                      })
                    }
                    className="ms-3 text-slate-500 hover:underline disabled:opacity-50"
                  >
                    {t("screens.dataImportView.historyDiscard")}
                  </button>
                )}
                {b.status === "committed" && confirmUndo !== b.id && (
                  <button
                    type="button"
                    data-testid={`import-history-undo-${b.id}`}
                    disabled={undo.isPending}
                    onClick={() => setConfirmUndo(b.id)}
                    className="ms-3 text-slate-500 hover:underline disabled:opacity-50"
                  >
                    {t("screens.dataImportView.historyUndo")}
                  </button>
                )}
                {b.status === "committed" && confirmUndo === b.id && (
                  <span
                    data-testid={`import-history-undo-confirm-row-${b.id}`}
                    className="ms-3 inline-flex flex-wrap items-center gap-2"
                  >
                    <span className="text-slate-700">
                      {t("screens.dataImportView.historyUndoConfirmBody", {
                        filename: b.filename ?? "",
                      })}
                    </span>
                    <button
                      type="button"
                      data-testid={`import-history-undo-cancel-${b.id}`}
                      onClick={() => setConfirmUndo(null)}
                      className="text-slate-500 hover:underline"
                    >
                      {t("screens.dataImportView.historyUndoCancel")}
                    </button>
                    <button
                      type="button"
                      data-testid={`import-history-undo-confirm-${b.id}`}
                      disabled={undo.isPending}
                      onClick={() =>
                        undo.mutate(b.id, {
                          onSuccess: (result) => {
                            refresh();
                            void qc.invalidateQueries({
                              queryKey: importKeys.batch(b.id),
                            });
                            /* Partial by design -- a record the estate
                             has built on, or one a person has corrected
                             since, is NAMED rather than forced, and the
                             toast has to say so or the operator reads a
                             partial reversal as a complete one. */
                            const message = t("screens.dataImportView.historyUndone", {
                              removed: t(
                                "screens.dataImportView.historyUndoneRemoved",
                                {
                                  count: result.reverted_inserts,
                                },
                              ),
                              restored: t(
                                "screens.dataImportView.historyUndoneRestored",
                                {
                                  count: result.reverted_updates,
                                },
                              ),
                            });
                            if (result.refused.length > 0) {
                              toast.push({
                                tone: "warning",
                                message,
                                detail: t("screens.dataImportView.historyUndoRefused", {
                                  count: result.refused.length,
                                  reason: result.refused[0]!.reason,
                                }),
                              });
                            } else {
                              toast.push({ tone: "success", message });
                            }
                          },
                          onError: (err) => {
                            if (err instanceof ImportUndoUnsure) {
                              refresh();
                              toast.push({
                                tone: "warning",
                                message: t("screens.dataImportView.historyUndoUnsure"),
                              });
                              return;
                            }
                            toast.push({
                              tone: "error",
                              message: t("screens.dataImportView.historyUndoFailed"),
                              // F1 -- the server's sentence, not "API 409: ...".
                              detail: refusalMessage(t, err),
                            });
                          },
                          onSettled: () => setConfirmUndo(null),
                        })
                      }
                      className="font-medium text-rose-700 hover:underline disabled:opacity-50"
                    >
                      {t("screens.dataImportView.historyUndoConfirm")}
                    </button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/**
 * Round 16 I14 -- a row error's nouns and hints in the reader's words.
 * The entity arrives as a code ("service") and the expected form as a
 * code and its values; the server's English stays the fallback. The
 * field name is the column header as the bank's file spells it, so it
 * stays the identifier it is.
 */
function rowErrorWords(
  t: TFunction,
  params: Record<string, unknown>,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof params.entity === "string" && params.entity) {
    const noun = entityKindLabel(params.entity);
    // Lower-case the first letter of a word ("Service" -> "service"),
    // never an acronym ("IBS").
    out.entity =
      noun.length > 1 && noun[1] === noun[1]!.toLowerCase()
        ? noun[0]!.toLowerCase() + noun.slice(1)
        : noun;
  }
  // Round 20 W1 -- a misshapen row's line and counts, grouped the way
  // the reader groups figures, and the row's name when it has one.
  for (const k of ["line", "fields", "header"] as const) {
    if (typeof params[k] === "number")
      out[k] = String(groupedCount(params[k] as number, i18n.language));
  }
  if ("label" in params)
    out.who =
      typeof params.label === "string" && params.label ? `, ${params.label}` : "";
  if (typeof params.form_key === "string") {
    const key = `import.form.${params.form_key}`;
    if (i18n.exists(key))
      out.form = t(key, {
        values: params.form_values ?? "",
        example: params.form_example ?? "",
        max: params.form_max ?? "",
      });
  }
  return out;
}

export function DataImportView() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  const dict = useImportDictionary({ enabled: isAdmin });
  const upload = useUploadCsv();
  const commit = useCommitBatch();
  const toast = useToast();
  const qc = useQueryClient();

  /* Round 12 A6 -- an empty register's route lands here with its own
     entity named, so the operator does not re-find in a list of
     seventeen what the page he came from already knew. Validated
     against the dictionary rather than trusted: a stale or hand-typed
     kind selects nothing instead of a kind that does not exist. */
  const [params] = useSearchParams();
  const requestedKind = params.get("kind") ?? "";
  /* ROUND 13 D8 -- `?batch=<id>` was ignored for the whole of the
     walk, so a link to a staged review led to an empty upload form and
     the operator had no route back to work he had already done. */
  const requestedBatch = params.get("batch") ?? "";
  const [openBatchId, setOpenBatchId] = useState("");
  const [kind, setKind] = useState("");
  const [file, setFile] = useState<File | null>(null);
  // E8 -- the refusal stays beside the file input until a smaller file is chosen.
  const [tooLarge, setTooLarge] = useState(false);
  const [assisted, setAssisted] = useState(false);
  const [autoCommit, setAutoCommit] = useState(false);
  const [resolveEnums, setResolveEnums] = useState(false);
  const [batch, setBatch] = useState<ImportBatch | null>(null);
  // Round 20 W9 -- batches discarded on this page: "Discarded. Nothing
  // had been written." stood above the discarded batch's review and its
  // "Commit 787 OK rows", inviting a commit of a batch that was gone.
  const [discarded, setDiscarded] = useState<string[]>([]);
  const wanted = openBatchId || requestedBatch;
  const fetched = useImportBatch(wanted, { enabled: Boolean(wanted) });

  // Applied once the dictionary lands, and only if the operator has not
  // already chosen for himself.
  useEffect(() => {
    if (!requestedKind || kind) return;
    const known = (dict.data?.entities ?? []).some((e) => e.kind === requestedKind);
    if (known) setKind(requestedKind);
  }, [requestedKind, kind, dict.data]);

  /* The fetched batch becomes the one on screen. Its own entity is
     selected with it, so the review and the upload form above it are
     never describing two different things. */
  useEffect(() => {
    if (!fetched.data || discarded.includes(fetched.data.id)) return;
    setBatch(fetched.data);
    // Round 20 W13 -- a batch opened from Earlier uploads may be another
    // kind's (staged batches of every kind are listed); its own kind is
    // selected with it.
    setKind((current) =>
      openBatchId && fetched.data.id === openBatchId
        ? fetched.data.entity_kind
        : current || fetched.data.entity_kind,
    );
  }, [fetched.data, discarded, openBatchId]);

  if (!isAdmin) {
    return <AdminOnlyPanel surface="Data import" />;
  }

  const entities = dict.data?.entities ?? [];
  const selectedEntity = entities.find((e) => e.kind === kind);
  const selectedIsLink = selectedEntity?.is_link ?? false;
  const selectedIsChild = selectedEntity?.is_child ?? false;
  // Graph edges (P150) + owned children (P259) import via the structured
  // CSV path only -- never Engine-assisted.
  const structuredOnly = selectedIsLink || selectedIsChild;
  const canUpload = Boolean(kind && file) && !upload.isPending;

  const onUpload = () => {
    // ROUND 15 E8 -- a 70 MB file on the host read "Upload failed /
    // Failed to fetch": the edge closed the connection before the
    // product's own 413 sentence could be sent, and the browser reported
    // a network error. The size is known here before a byte moves, so
    // the sentence is drawn here and nothing is sent. The server stays
    // the thing that decides (round 13 D3): a 5.92 MB file still draws
    // its 422 from there.
    if (file && file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setTooLarge(true);
      toast.push({
        tone: "warning",
        message: t("screens.dataImportView.tooLargeToSend", {
          megabytes: MAX_UPLOAD_MB,
        }),
      });
      return;
    }
    setTooLarge(false);
    if (!kind || !file) return;
    const useAssisted = assisted && !structuredOnly;
    upload.mutate(
      // Graph edges aren't Engine-assistable -- never send assisted for them.
      {
        kind,
        file,
        assisted: useAssisted,
        autoCommit: useAssisted && autoCommit,
        resolveEnums: useAssisted && resolveEnums,
      },
      {
        onSuccess: (b) => {
          setBatch(b);
          void qc.invalidateQueries({ queryKey: importKeys.batches(kind) });
          const committed = b.status === "committed";
          toast.push({
            tone: b.error_count > 0 ? "warning" : "success",
            message: committed
              ? t("screens.dataImportView.committedRows", {
                  ok: b.ok_count,
                  count: b.row_count,
                })
              : t("screens.dataImportView.stagedRows", {
                  ok: b.ok_count,
                  count: b.row_count,
                }),
            detail:
              b.error_count > 0
                ? t("screens.dataImportView.rowsHaveErrors", { count: b.error_count })
                : committed
                  ? "Engine mapped + auto-committed the clean rows"
                  : useAssisted
                    ? "Engine mapped your columns -- review then commit"
                    : "Review then commit",
          });
        },
        onError: (err) =>
          toast.push({
            tone: "error",
            message:
              err.status === 503 ? "Marsad Engine not configured" : "Upload failed",
            // ROUND 15 E9 -- a refusal that carries a count renders it
            // grouped in the reader's locale, not as the server wrote it.
            // Round 20 W13 -- no answer at all ("Failed to fetch") is the
            // browser failing to read or send the file, never a server
            // verdict: say so, and what to try.
            detail:
              err instanceof ImportError
                ? refusalMessage(t, err)
                : t("screens.dataImportView.notSent"),
          }),
      },
    );
  };

  const onCommit = () => {
    if (!batch) return;
    commit.mutate(batch.id, {
      onSuccess: (b) => {
        /* Round 12 A6 -- this merged only the status and kept the
           STAGED rows, whose committed ids are null by definition. So
           F4's "See the row you imported" could never find the one row
           it was written for, and every commit fell back to the plain
           register. The commit answers with the detail shape now, so
           take it whole. */
        setBatch(b);
        void qc.invalidateQueries({ queryKey: importKeys.completeness });
        void qc.invalidateQueries({ queryKey: importKeys.batches(kind) });
        toast.push({
          tone: "success",
          message: t("screens.dataImportView.committedInsertedUpdated", {
            inserted: b.insert_count,
            updated: b.update_count,
          }),
        });
      },
      onError: (err) =>
        toast.push({
          tone: "error",
          message: t("screens.dataImportView.commitFailed"),
          detail: refusalMessage(t, err),
        }),
    });
  };

  return (
    <article data-testid="data-import-view" className="max-w-5xl">
      <header className="mb-5">
        <h1 className="text-2xl font-semibold text-slate-900">
          <MarsadIcon
            name="document-locker"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.dataImportView.dataImport")}
        </h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">
          {t("screens.dataImportView.uploadACsvToBulkCreateOr")}{" "}
          {/* Round 17 F9 -- the dictionary named and not linked, and the
              order nobody stated here (Set up says it). */}
          <Link
            to="/admin/data-dictionary"
            data-testid="import-dictionary-link"
            className="text-brand-brassDark hover:underline"
          >
            {t("screens.dataImportView.openDictionary")}
          </Link>
        </p>
        <p
          data-testid="import-order"
          className="mt-1 max-w-prose text-xs text-slate-500"
        >
          {t("screens.dataImportView.loadOrder")}
        </p>
      </header>

      <CompletenessPanel />

      <section
        data-testid="import-upload"
        className="mb-6 rounded-lg border border-slate-200 bg-white p-4"
      >
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              {t("screens.dataImportView.entity")}
            </span>
            <select
              data-testid="import-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
            >
              <option value="">{t("screens.dataImportView.selectAnEntity")}</option>
              {/* Round 11 F5 -- the same words and the same arrow as
                  the dictionary one click away, which was rendering
                  "Business service → Service" while this printed the
                  server's English prose, "IBS -> Service link". An
                  option takes text, so the trail is composed rather
                  than rendered -- by the same helper, so the two
                  cannot disagree again. */}
              {entities.map((e) => (
                <option key={e.kind} value={e.kind}>
                  {(e.label_hops ?? []).length > 0
                    ? pathTrailText(
                        e.label_hops ?? [],
                        "screens.dataDictionaryView.hop",
                        t,
                        i18n.dir() === "rtl",
                      )
                    : said(t, `screens.dataImportView.entityLabel.${e.kind}`, e.label)}
                </option>
              ))}
            </select>
            {/* Round 20 W4 -- the bank's GRC extract found no entity
                here and no word why. Name what does not import. */}
            {(dict.data?.not_importable ?? []).length > 0 && (
              <span
                data-testid="import-not-importable"
                className="mt-1 block max-w-xs text-xs text-slate-500"
              >
                {t("screens.dataImportView.notImportable", {
                  kinds: listOf(
                    (dict.data?.not_importable ?? []).map((n) =>
                      said(
                        t,
                        `screens.dataDictionaryView.notImportable.${n.kind}.short`,
                        n.kind,
                      ),
                    ),
                  ),
                })}{" "}
                <Link
                  to="/admin/data-dictionary#not-importable"
                  className="text-brand-brassDark hover:underline"
                >
                  {t("screens.dataImportView.notImportableWhy")}
                </Link>
              </span>
            )}
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              {assisted
                ? t("screens.dataImportView.csvJsonXlsxFile")
                : t("screens.dataImportView.csvFile")}
            </span>
            <input
              data-testid="import-file"
              type="file"
              // P233: JSON; P234: .xlsx -- both only on the Engine-assisted path.
              accept={
                assisted
                  ? ".csv,text/csv,.json,application/json,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  : ".csv,text/csv"
              }
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setTooLarge(false);
              }}
              className="text-sm"
            />
            {/* ROUND 13 D3 -- the ceiling, before a file is chosen.
                The limits were reachable only by hitting them: a 2.1 MB
                file built from the product's own template came back as a
                web server's error page, and the 5,000-row cap was a
                sentence in a 422. Stated here, both are a thing to plan
                a split around rather than a thing to discover. */}
            <span
              data-testid="import-limits"
              className="mt-1 block text-[11px] text-slate-500"
            >
              {t("screens.dataImportView.uploadLimits", {
                megabytes: MAX_UPLOAD_MB,
                rows: MAX_IMPORT_ROWS,
              })}
            </span>
            {tooLarge && (
              <span
                data-testid="import-too-large"
                className="mt-1 block rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
              >
                {t("screens.dataImportView.tooLargeToSend", {
                  megabytes: MAX_UPLOAD_MB,
                })}
              </span>
            )}
          </label>
          <button
            type="button"
            data-testid="import-upload-button"
            onClick={onUpload}
            disabled={!canUpload}
            aria-busy={upload.isPending}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
          >
            {upload.isPending ? (
              assisted ? (
                // The assisted path is the slow Engine call -> liveness.
                <EngineBusy label={t("screens.dataImportView.mappingWithEngine")} />
              ) : (
                t("screens.dataImportView.validating")
              )
            ) : assisted ? (
              t("screens.dataImportView.mapWithEngineValidate")
            ) : (
              t("screens.dataImportView.uploadValidate")
            )}
          </button>
        </div>
        {structuredOnly ? (
          <p data-testid="import-link-hint" className="mt-3 text-xs text-slate-500">
            {selectedIsChild
              ? t("screens.dataImportView.journeyStepsAttachToAnExistingJourney")
              : t("screens.dataImportView.graphEdgesLinkTwoExistingEntitiesBy")}
          </p>
        ) : (
          <>
            <label className="mt-3 flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                data-testid="import-assisted-toggle"
                checked={assisted}
                onChange={(e) => setAssisted(e.target.checked)}
              />
              {t("screens.dataImportView.engineAssistedLetTheMarsadEngineMap")}
            </label>
            {assisted && (
              <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  data-testid="import-auto-commit-toggle"
                  checked={autoCommit}
                  onChange={(e) => setAutoCommit(e.target.checked)}
                />
                {t("screens.dataImportView.autoCommitWhenCleanApplyTheMapped")}
              </label>
            )}
            {assisted && (
              <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  data-testid="import-resolve-enums-toggle"
                  checked={resolveEnums}
                  onChange={(e) => setResolveEnums(e.target.checked)}
                />
                {t("screens.dataImportView.resolveEnumSynonymsWithTheEngineMap")}
              </label>
            )}
          </>
        )}
      </section>

      <BatchHistory
        kind={kind}
        onOpen={(id) => {
          setOpenBatchId(id);
          void qc.invalidateQueries({ queryKey: importKeys.batch(id) });
        }}
        onDiscarded={(id) => {
          setDiscarded((d) => [...d, id]);
          setBatch((b) => (b?.id === id ? null : b));
          setOpenBatchId((o) => (o === id ? "" : o));
        }}
      />

      {batch && <EngineMappingPanel batch={batch} />}
      {batch && (
        <BatchReview batch={batch} onCommit={onCommit} committing={commit.isPending} />
      )}
    </article>
  );
}
